// /src/pages/Settings.js

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addIp,
  removeIp,
  setCallbackUrl,
  addApiKey,
  removeApiKey,
  fetchApiKeys,
} from "../store/actions/settingsActions";
import {
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  ContentCopy,
  Delete,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ConfirmDialog from "../components/Dialog";
import { useTranslation } from "react-i18next";
import { logoutUser } from "../store/actions/authActions";
import { MdLogout } from "react-icons/md";
import { useTheme, useMediaQuery } from "@mui/material";

const Settings = () => {
  const dispatch = useDispatch();
  const {
    ipList,
    senderNumbers,
    selectedSenderNumber,
    callbackUrl,
    callbackServerIp,
    callbackApiKey,
    apiKeys,
    clientId,
  } = useSelector((state) => state.settings);

  const { t } = useTranslation(); // Get the t function

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unmaskedKeys, setUnmaskedKeys] = useState({});
  // State variables for confirmation dialog
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedNumberValue, setSelectedNumberValue] = useState(
    selectedSenderNumber || ""
  );
  const [showAddNewNumberForm, setShowAddNewNumberForm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState("");
  const [confirmDialogTitle, setConfirmDialogTitle] = useState("");
  const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
  // Update selectedNumberValue when selectedSenderNumber changes
  useEffect(() => {
    if (selectedNumberValue !== "add-new") {
      setSelectedNumberValue(selectedSenderNumber);
    }
  }, [selectedSenderNumber]);
  useEffect(() => {
    dispatch(fetchApiKeys())
      .unwrap()
      .catch((error) => {
        const code = typeof error === "string" ? error : error?.code;
        setSnackbarMessage(t(`notifications.${code}`) || t("notifications.failedToFetch"));
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      });
  }, [dispatch]);

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleAddApiKey = () => {
    setIsSubmitting(true);
    dispatch(addApiKey())
      .unwrap()
      .then(() => {
        setSnackbarMessage(t("notifications.newApiKeyCreated"));
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
        setIsSubmitting(false);
      })
      .catch((error) => {
        const code = typeof error === "string" ? error : error?.code;
        setSnackbarMessage(t(`notifications.${code}`) || t("notifications.failedToCreate"));
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        setIsSubmitting(false);
      });
  };

  const handleRemoveApiKey = (apiKeyId) => {
    dispatch(removeApiKey(apiKeyId))
      .unwrap()
      .then(() => {
        setSnackbarMessage(t("notifications.removed"));
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      })
      .catch((error) => {
        const code = typeof error === "string" ? error : error?.code;
        setSnackbarMessage(t(`notifications.${code}`) || t("notifications.failedToDelete"));
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      });
  };

  // New handler for removing IP addresses
  const handleRemoveIp = (ip) => {
    dispatch(removeIp({ removeMe: ip, existingIps: ipList }))
      .unwrap()
      .then(() => {
        setSnackbarMessage(t("notifications.removed"));
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      })
      .catch((error) => {
        const code = typeof error === "string" ? error : error?.code;
        setSnackbarMessage(t(`notifications.${code}`) || t("notifications.failedToDelete"));
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      });
  };

  // Handler to open the confirmation dialog
  const handleOpenConfirmDialog = (item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setOpenConfirmDialog(true);

    if (type === "apiKey") {
      setConfirmDialogTitle(t("settings.deleteKey"));
      setConfirmDialogMessage(t("settings.deleteKeyDesc"));
    } else if (type === "ip") {
      setConfirmDialogTitle(t("settings.deleteIp"));
      setConfirmDialogMessage(t("settings.deleteIpDesc"));
    }
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setItemToDelete(null);
    setDeleteType("");
    setConfirmDialogTitle("");
    setConfirmDialogMessage("");
  };
  const handleConfirmDelete = () => {
    if (itemToDelete) {
      if (deleteType === "apiKey") {
        handleRemoveApiKey(itemToDelete.id);
      } else if (deleteType === "ip") {
        handleRemoveIp(itemToDelete);
      }
      handleCloseConfirmDialog();
    }
  };

  // Validation Schemas
  const IpSchema = Yup.object().shape({
    newIp: Yup.string()
      .matches(
        /^(?:(?:25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(?:\.(?:25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}|(?:[A-Fa-f0-9]{1,4}:){7}[A-Fa-f0-9]{1,4})$/,
        "Invalid IP address format"
      )
      .required(t("settings.ipIsRequired")),
  });

  const SenderSchema = Yup.object().shape({
    newSenderNumber: Yup.string()
      .matches(/^[1-9]\d{1,14}$/, t("common.invalidPhone"))
      .required(t("settings.senderIsRequired")),
  });

  const CallbackUrlSchema = Yup.object().shape({
    callbackUrl: Yup.string()
      .url(t("common.invalidUrl"))
      .required(t("settings.callbackIsRequired")),
  });

  const ApiKeySchema = Yup.object().shape({
    apiKeys: Yup.array().of(Yup.string()),
  });

  const [showCallbackApiKey, setShowCallbackApiKey] = useState(false);

  const displayedCallbackApiKey = showCallbackApiKey
    ? callbackApiKey
      ? "Bearer " + callbackApiKey
      : t("notifications.failedToFetch")
    : callbackApiKey
      ? "•".repeat(callbackApiKey.length)
      : t("notifications.failedToFetch");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      p={3}
      sx={{
        maxWidth: "800pt",
        mx: "auto",
        width: "100%"
      }}>
      {/* Whitelisted IPs Section */}
      <Paper elevation={3} sx={{ padding: 2, marginBottom: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t("settings.whitelistedIps")}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t("settings.whitelistedIpsDesc")}
        </Typography>
        <Formik
          initialValues={{ newIp: "" }}
          validationSchema={IpSchema}
          onSubmit={(values, { resetForm }) => {
            dispatch(addIp({ newIp: values.newIp, existingIps: ipList }))
              .unwrap()
              .then(() => {
                setSnackbarMessage(t("notifications.ipAdded"));
                setSnackbarSeverity("success");
                setOpenSnackbar(true);
                resetForm();
              })
              .catch((error) => {
                setSnackbarMessage(t("notifications.failedToAddIp"));
                setSnackbarSeverity("error");
                setOpenSnackbar(true);
              });
          }}
        >
          {({ values, handleChange, handleBlur, errors, touched }) => (
            <Form>
              <Grid container spacing={2}>
                {ipList.map((ip, index) => (
                  <Grid item xs={12} key={index}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="body1">{ip}</Typography>
                      <IconButton
                        color="error"
                        onClick={() => handleOpenConfirmDialog(ip, "ip")}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
                {ipList.length < 10 && (
                  <>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label={t("settings.newIpAddress")}
                        name="newIp"
                        value={values.newIp}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.newIp && Boolean(errors.newIp)}
                        helperText={touched.newIp && errors.newIp}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        fullWidth
                        sx={{ height: "100%" }}
                      >
                        {t("common.add")}
                      </Button>
                    </Grid>
                  </>
                )}
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>

      {/* Client ID (Read-only) */}
      <Paper elevation={3} sx={{ padding: 2, marginBottom: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t("settings.clientId")}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t("settings.clientIdDesc")}
        </Typography>
        <Box display="flex" alignItems="center">


          <TextField
            fullWidth
            value={clientId || t("notifications.failedToFetch")}
            disabled
          />
          <Tooltip title="Copy Client ID">
            <IconButton
              onClick={() => {
                navigator.clipboard
                  .writeText(clientId)
                  .then(() => {
                    setSnackbarMessage(
                      t("notifications.copiedToClipboard")
                    );
                    setSnackbarSeverity("success");
                    setOpenSnackbar(true);
                  })
                  .catch(() => {
                    setSnackbarMessage(t("notifications.failedToCopy"));
                    setSnackbarSeverity("error");
                    setOpenSnackbar(true);
                  });
              }}
            >
              <ContentCopy />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Callback URL Section */}
      <Paper elevation={3} sx={{ padding: 2, marginBottom: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t("settings.callbackUrl")}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t("settings.callbackUrlDesc")}
        </Typography>
        <Formik
          initialValues={{ callbackUrl: callbackUrl || "" }}
          enableReinitialize={true}
          validationSchema={CallbackUrlSchema}
          onSubmit={(values, { setSubmitting }) => {
            dispatch(setCallbackUrl(values.callbackUrl))
              .unwrap()
              .then(() => {
                setSnackbarMessage(t("notifications.callbackUpdated"));
                setSnackbarSeverity("success");
                setOpenSnackbar(true);
                setSubmitting(false);
              })
              .catch((error) => {
                setSnackbarMessage(t("notifications.failedToUpdateCallback"));
                setSnackbarSeverity("error");
                setOpenSnackbar(true);
                setSubmitting(false);
              });
          }}
        >
          {({
            values,
            handleChange,
            handleBlur,
            errors,
            touched,
            isSubmitting,
          }) => (
            <Form>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={10}>
                  <TextField
                    fullWidth
                    label={t("settings.callbackUrlLabel")}
                    name="callbackUrl"
                    value={values.callbackUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.callbackUrl && Boolean(errors.callbackUrl)}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    disabled={isSubmitting}
                    sx={{ height: "100%" }}
                  >
                    {isSubmitting ? t("common.saving") : t("common.save")}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>


      {/* Callback Server IP (Read-only) */}
      <Paper elevation={3} sx={{ padding: 2, marginBottom: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t("settings.callbackServerIp")}
        </Typography>

        <Box display="flex" alignItems="center" mt={2}>

          <TextField
            label={t("settings.callbackIpLabel")}
            fullWidth
            value={callbackServerIp || t("notifications.failedToFetch")}
            disabled
            helperText={t("settings.callbackServerIpDesc")}
          />
        </Box>

        {/* Callback API Key with show/hide toggle */}
        <Box display="flex" alignItems="center" mt={2}>
          <TextField
            label={t("settings.callbackApiKeyLabel")}
            fullWidth
            value={displayedCallbackApiKey}
            disabled
            sx={{ flexGrow: 1 }}
            helperText={t("settings.callbackApiKeyDesc")}
          />
          <Tooltip
            title={showCallbackApiKey ? t("common.hide") : t("common.show")}
          >
            <IconButton
              onClick={() => setShowCallbackApiKey(!showCallbackApiKey)}
            >
              {showCallbackApiKey ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* API Keys Section */}
      <Paper elevation={3} sx={{ padding: 2, marginBottom: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t("settings.apiKeys")}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t("settings.apiKeysDesc")}
        </Typography>
        <Grid container spacing={2}>
          {apiKeys.map((apiKeyObj) => {
            const isUnmasked = unmaskedKeys[apiKeyObj.id] || false;
            const displayedKey = isUnmasked
              ? apiKeyObj.key
              : "•".repeat(apiKeyObj.key.length);

            return (
              <Grid item xs={12} key={apiKeyObj.id}>
                <Box display="flex" alignItems="center">
                  <TextField
                    fullWidth
                    value={displayedKey}
                    disabled
                    sx={{ flexGrow: 1 }}
                  />
                  <Tooltip
                    title={isUnmasked ? t("common.hide") : t("common.show")}
                  >
                    <IconButton
                      onClick={() => {
                        setUnmaskedKeys((prevState) => ({
                          ...prevState,
                          [apiKeyObj.id]: !prevState[apiKeyObj.id],
                        }));
                      }}
                    >
                      {isUnmasked ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy API Key">
                    <IconButton
                      onClick={() => {
                        navigator.clipboard
                          .writeText(apiKeyObj.key)
                          .then(() => {
                            setSnackbarMessage(
                              t("notifications.copiedToClipboard")
                            );
                            setSnackbarSeverity("success");
                            setOpenSnackbar(true);
                          })
                          .catch(() => {
                            setSnackbarMessage(t("notifications.failedToCopy"));
                            setSnackbarSeverity("error");
                            setOpenSnackbar(true);
                          });
                      }}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete API Key">
                    <IconButton
                      color="error"
                      // onClick={() => handleRemoveApiKey(apiKeyObj.id)}
                      onClick={() =>
                        handleOpenConfirmDialog(apiKeyObj, "apiKey")
                      }
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            );
          })}
          {apiKeys.length < 3 && (
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddApiKey}
                fullWidth
                disabled={isSubmitting}
              >
                {isSubmitting ? t("common.getting") : t("common.getNew")}
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>
      {/* Confirmation Dialog */}
      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmDelete}
        title={confirmDialogTitle}
        message={confirmDialogMessage}
        item={
          itemToDelete
            ? deleteType === "apiKey"
              ? itemToDelete.key
              : itemToDelete
            : ""
        }
        confirmButtonText="Delete"
        confirmButtonColor="error"
      />
      {/* Snackbar for Notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {isMobile && (
        <Button
          variant="contained"
          color="error"
          startIcon={<MdLogout />}
          onClick={() => dispatch(logoutUser())}
          fullWidth
        >
          {t("login.logout")}
        </Button>)}
    </Box >
  );
};

export default Settings;
